import { localized } from "@msuite/ui/localization";
import { createColumnHelper } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import {
	getSuborderNumberString,
	getTransportOrderEventColor,
	getTransportOrderEventTranslationKey,
} from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import type { TransportOrderEventListItem } from "@msuite/utils/types";

const columnHelper = createColumnHelper<TransportOrderEventListItem>();

/**
 * The chain of custody as a table: one row per scan, oldest first for a leg and grouped by box for
 * a whole round.
 *
 * `carrier` and `suborder` are hidden by the single-leg view through `initialColumnVisibility`,
 * where both are constant across every row. One column set rather than two, for the reason the
 * protocol's states it.
 */
export const transportEventsTableColumns = [
	columnHelper.accessor("event", {
		id: "event",
		header: localized("general.type"),
		cell: (cell) => (
			<Tag
				variant="plain"
				// The one event a reader is hunting for, so it pings: a handover is where the leg row
				// stops being the truth, because it overwrites the holder it replaced.
				withPing={cell.getValue() === "taken-over"}
				color={getTransportOrderEventColor(cell.getValue())}
				className="-mx-2 px-2"
			>
				{localized(getTransportOrderEventTranslationKey(cell.getValue()))}
			</Tag>
		),
	}),
	columnHelper.accessor((row) => `${row.loadCarrierName} ${row.unitNumber}/${row.unitTotal}`, {
		id: "carrier",
		header: localized("stock.load_carrier"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor("fromZoneName", {
		id: "fromZoneName",
		header: localized("stock.transport_from_zone"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("toZoneName", {
		id: "toZoneName",
		header: localized("stock.transport_to_zone"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor(
		(row) =>
			getSuborderNumberString({
				orderNumber: row.orderNumber,
				suborderNumber: row.suborderNumber,
			}),
		{
			id: "suborder",
			header: localized("stock.suborder"),
			cell: (cell) => cell.getValue(),
		},
	),
	columnHelper.accessor("changedAt", {
		id: "changedAt",
		header: localized("general_fields.created_at"),
		cell: (cell) => moment(cell.getValue()).shortFormat(),
	}),
	columnHelper.accessor("changedByName", {
		id: "changedByName",
		header: localized("general_fields.created_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
];
